import { Button } from '@mui/material';
import { useEffect, useState } from 'react';
import { Calendar } from 'react-multi-date-picker';
import { dateFormatMMddyyyy } from '../../../../../AppConstants';
import CustomDialog from '../../../../../components/dialog/CustomDialog';
import { ensureKeyboardTabbableElements } from '../../../../../components/date/datePickerFocusTrap';
import { getEmailLessonPlanForStudent } from '../../../../../services/LessonService';
import { useDomain } from '../../../../../states/AppDomainProvider';
import Logger from '../../../../../utils/Logger';
import { useGetRemoteServiceWrapper } from '../../../../hooks/RemoteServiceHooks';
import { useGetCurrentStudent } from '../../../../hooks/StudentHooks';
import './EmailLessonPlanAction.scss';

const minDate = new Date();
minDate.setHours(0);
minDate.setMinutes(0);
minDate.setSeconds(0);
minDate.setMilliseconds(0);

const dateFormat = dateFormatMMddyyyy.toUpperCase();

export default function EmailLessonPlanAction() {
  const [selectedDates, setSelectedDates] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const { callRemoteServiceWrapper, loading } = useGetRemoteServiceWrapper();
  const { uiDomain } = useDomain();
  const currentStudent = useGetCurrentStudent();

  const handleOnClick = () => {
    setOpenDialog(true);
  };

  const handleOnDialogClose = () => {
    setOpenDialog(false);
  };

  useEffect(() => {
    if (openDialog) {
      setSelectedDates([]);
    }
  }, [openDialog]);

  useEffect(() => {
    if (!openDialog) {
      return () => {};
    }

    const getCalendarOverlayElement = () => (
      document.querySelector([
        '.import-licenses-action-dialog .rmdp-wrapper',
        '.import-licenses-action-dialog .rmdp-calendar',
        '.MuiDialog-root .rmdp-wrapper',
        '.MuiDialog-root .rmdp-calendar',
      ].join(', '))
    );

    const isVisible = (element) => Boolean(
      element
      && (element.offsetWidth || element.offsetHeight || element.getClientRects().length),
    );

    const getFirstVisiblePickerElement = (calendarOverlayElement) => {
      const yearPicker = calendarOverlayElement.querySelector('.rmdp-year-picker');
      const monthPicker = calendarOverlayElement.querySelector('.rmdp-month-picker');
      const dayPicker = calendarOverlayElement.querySelector('.rmdp-day-picker');

      const isPickerInBlockDisplay = (pickerElement) => (
        pickerElement
        && pickerElement.style
        && pickerElement.style.display === 'block'
        && isVisible(pickerElement)
      );

      let activePicker = dayPicker;
      if (isPickerInBlockDisplay(yearPicker)) {
        activePicker = yearPicker;
      } else if (isPickerInBlockDisplay(monthPicker)) {
        activePicker = monthPicker;
      }

      if (!activePicker || !isVisible(activePicker)) {
        return null;
      }

      return activePicker.querySelector([
        '.rmdp-day.rmdp-selected:not(.rmdp-day-hidden):not(.rmdp-disabled)',
        '.rmdp-day.rmdp-today:not(.rmdp-day-hidden):not(.rmdp-disabled)',
        '.rmdp-day:not(.rmdp-day-hidden):not(.rmdp-disabled)',
        '.rmdp-month:not(.rmdp-disabled)',
        '.rmdp-year:not(.rmdp-disabled)',
      ].join(', '));
    };

    const getFirstPickerGridElement = (calendarOverlayElement) => {
      const yearPicker = calendarOverlayElement.querySelector('.rmdp-year-picker');
      const monthPicker = calendarOverlayElement.querySelector('.rmdp-month-picker');
      const dayPicker = calendarOverlayElement.querySelector('.rmdp-day-picker');

      const isPickerInBlockDisplay = (pickerElement) => (
        pickerElement
        && pickerElement.style
        && pickerElement.style.display === 'block'
        && isVisible(pickerElement)
      );

      let activePicker = dayPicker;
      if (isPickerInBlockDisplay(yearPicker)) {
        activePicker = yearPicker;
      } else if (isPickerInBlockDisplay(monthPicker)) {
        activePicker = monthPicker;
      }

      if (!activePicker || !isVisible(activePicker)) {
        return null;
      }

      return activePicker.querySelector(
        '.rmdp-day:not(.rmdp-day-hidden):not(.rmdp-disabled), .rmdp-month:not(.rmdp-disabled), .rmdp-year:not(.rmdp-disabled)',
      );
    };

    const handleRightChevronTab = (event) => {
      if (event.key !== 'Tab') {
        return;
      }

      const calendarOverlayElement = getCalendarOverlayElement();
      if (!calendarOverlayElement) {
        return;
      }

      const { target } = event;
      if (!target || !calendarOverlayElement.contains(target)) {
        return;
      }

      if (!event.shiftKey && target.classList && target.classList.contains('rmdp-right')) {
        const nextPickerElement = getFirstVisiblePickerElement(calendarOverlayElement);
        if (nextPickerElement && isVisible(nextPickerElement)) {
          event.preventDefault();
          nextPickerElement.focus();
        }
        return;
      }

      if (event.shiftKey) {
        const firstPickerItem = getFirstPickerGridElement(calendarOverlayElement);
        const rightChevron = calendarOverlayElement.querySelector('.rmdp-right');
        if (
          firstPickerItem
          && rightChevron
          && target === firstPickerItem
          && isVisible(rightChevron)
        ) {
          event.preventDefault();
          rightChevron.focus();
        }
      }
    };

    let retryTimeout = null;
    const observer = new MutationObserver(() => {
      const calendarOverlayElement = getCalendarOverlayElement();
      if (calendarOverlayElement) {
        ensureKeyboardTabbableElements(calendarOverlayElement);
      }
    });

    const attemptAttachKeyboardTabbable = (retriesLeft = 12) => {
      const calendarOverlayElement = getCalendarOverlayElement();
      if (calendarOverlayElement) {
        ensureKeyboardTabbableElements(calendarOverlayElement);
        observer.observe(calendarOverlayElement, { childList: true, subtree: true });
        return;
      }

      if (retriesLeft <= 0) {
        return;
      }

      retryTimeout = setTimeout(() => {
        attemptAttachKeyboardTabbable(retriesLeft - 1);
      }, 30);
    };

    attemptAttachKeyboardTabbable();
    document.addEventListener('keydown', handleRightChevronTab, true);

    return () => {
      if (retryTimeout) {
        clearTimeout(retryTimeout);
      }
      observer.disconnect();
      document.removeEventListener('keydown', handleRightChevronTab, true);
    };
  }, [openDialog]);

  const handleOnConfirm = async () => {
    try {
      await callRemoteServiceWrapper(async () => {
        const start = selectedDates[0].format(dateFormat);
        const end = selectedDates[1].format(dateFormat);
        await getEmailLessonPlanForStudent(currentStudent.id, start, end);
        handleOnDialogClose();
        await uiDomain.showSnackbar(
          true,
          'success',
          10000,
          <div>
            The assignment calendar for {currentStudent.firstName} {currentStudent.lastName} will be emailed to you in the next 5-10 mins.
            <br />
            Any assignments made today will not show up in the email.
          </div>,
        );
        setOpenDialog(false);
      });
    } catch (e) {
      Logger.logError({
        ERROR: e,
      });
    }
  };

  return (
    <>
      <Button
        variant='contained'
        className='btn-primary'
        onClick={handleOnClick}
        data-test='email-lesson-plan-action'
      >
        Email Assignment Calendar
      </Button>
      {openDialog && (
        <CustomDialog
          openDialog={openDialog}
          className='import-licenses-action-dialog'
          onClose={handleOnDialogClose}
          title='Email Assignment Calendar'
          tabIndex={0}
          content={(
            <div>
              <Calendar
                range
                animation
                value={selectedDates}
                format={dateFormat}
                minDate={minDate}
                className='email-lesson-plan-calendar'
                onChange={(dates) => {
                  setSelectedDates(dates);
                }}
                data-test='date-input-picker-component'
                tabIndex={0}
              />
            </div>
          )}
          displayCloseIcon
          actions={[{
            onClick: handleOnConfirm,
            className: 'btn-primary action-button',
            title: 'Ok',
            loading,
            disabled: loading || !selectedDates || selectedDates.length < 2,
          },
          ]}
        />
      )}
    </>
  );
}
